import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Avatar, Link } from "@mui/material";
import { useAppDispatch } from "store/hooks";
import { APILogout, AVATARGetOrgImageUrl, AVATARGetUserAvatarUrl } from "utils/api";
import { useTranslation } from "react-i18next";
import { initialsFromName } from "utils/utils";

import "./AssignmentHeader.scss";
import { authenticate } from "store/slices/auth";
import { AUTH_STATUS } from "components/Modals/AuthModal";
import { triggerOpenModal } from "store/slices/modals";
import { removeCognitoTokensFromLocalStorage } from "utils/cognito";
import { PageState, useAssignmentsContext } from "components/AssignmentsProvider";
import AssignmentProfilePopup from "components/AssignmentProfilePopup";

const useProfilePopup = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const open = Boolean(anchorEl);
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  /**
   * Handle logout with loading state
   * @param {Function} logoutFn - The logout function to execute
   */
  const handleLogout = async (logoutFn) => {
    setIsLoading(true);
    try {
      await logoutFn();
      handleClose();
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    anchorEl,
    open,
    isLoading,
    handleClick,
    handleClose,
    handleLogout
  };
};

const AssignmentHeader = () => {
  const { 
    user, 
    isLoggedIn, 
    organization, 
    handleOpenAuthModal,
    assessmentPageState
  } = useAssignmentsContext();
  
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  
  const { 
    anchorEl: anchorElAssignmentProfilePopup,
    open: openAssignmentProfilePopup,
    isLoading: logoutLoading,
    handleClick: handleClickAssignmentProfilePopup,
    handleClose: handleCloseAssignmentProfilePopup,
    handleLogout: baseHandleLogout
  } = useProfilePopup();

  const idAssignmentProfilePopup = openAssignmentProfilePopup ? "assignment-profile-popup" : undefined;

  /**
   * Enhanced logout that handles navigation and state updates
   */
  const handleLogout = async () => {
    await baseHandleLogout(async () => {
      await APILogout();
      removeCognitoTokensFromLocalStorage();
      dispatch(authenticate(false, null));
      // Small delay for store updates to process
      // setTimeout(() => navigate("/?login"), 50);
      setTimeout(() => window.location.reload(), 50);
    });
  };

  /**
   * Auth modal on direct URL access with login param
   */
  useEffect(() => {
    const handleCreatePasswordModal = () =>
      dispatch(triggerOpenModal("modal-auth", { status: AUTH_STATUS.ASSESSMENT_CREATE_PASSWORD }));
      
    if (searchParams.has("login") && !isLoggedIn) {
      handleOpenAuthModal();
    } else if (searchParams.has("create-password")) {
      handleCreatePasswordModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <div className="navbar-assignment-placeholder"></div>
      <section className="navbar-assignment">
        <div className="logo">
          {
            assessmentPageState === PageState.LOADING && (
              <div className="min-h-[41px] min-w-[178px] bg-gray-200 animate-pulse rounded-md flex flex-row items-center gap-4 p-2">
                <div className="my-auto w-6 h-6 bg-gray-300 rounded-full"></div>
                <div className="my-auto w-24 h-4 bg-gray-300 rounded-md"></div>
              </div>
            )
          }
          {assessmentPageState !== PageState.LOADING && organization && organization.banner && (
            <img className="!max-h-[41px]" src={AVATARGetOrgImageUrl(organization.banner)} alt="banner" />
          )}
          {assessmentPageState !== PageState.LOADING && !(organization && organization.banner) && (
            <img className="!max-h-[41px]" src={require("assets/logo.svg").default} alt="logo" />
          )}
        </div>

        <div className="actions">
          {isLoggedIn && user ? (
            <div className="toolbar">
              <Link
                component="button"
                underline="none"
                className="profile_icon hover:cursor-pointer"
                onClick={handleClickAssignmentProfilePopup}
              >
                <Avatar
                  className="profile_icon !bg-[#d9d9d9] !font-poppins !text-[12px] font-semibold !text-black"
                  src={AVATARGetUserAvatarUrl(user)}
                  alt="profile"
                  sx={{
                    border: "2px solid rgba(64, 126, 201, 1)",
                  }}
                >
                  {initialsFromName((user.firstname ? user.firstname : "") + " " + (user.lastname ? user.lastname : ""))}
                  </Avatar>
              </Link>
              <AssignmentProfilePopup
                id={idAssignmentProfilePopup}
                open={openAssignmentProfilePopup}
                anchorEl={anchorElAssignmentProfilePopup}
                handleClose={handleCloseAssignmentProfilePopup}
                handleLogout={handleLogout}
                logoutLoading={logoutLoading}
              />
            </div>
          ) : (
            <Link onClick={handleOpenAuthModal} className="login">
              {t("sign-in")}
            </Link>
          )}
        </div>
      </section>
    </>
  );
};

export default AssignmentHeader;